import Link from "next/link";
import { translate } from "@locales";
import { useHelpSearch } from "@models/interactions/use-help-search";
import Icon from "@components/atoms/icon";
import Input from "@components/atoms/input";
import HelpCenterLayout from "@components/templates/help-center-layout";
import BackgroundImage from "@assets/img/help/help-bg.png";
import HELP from "@help/index.json";

type Section = {
  name: string;
  description: string;
};

interface ClearSearchProps {
  onClick?: () => void;
}

function useHelpCenterIndex() {
  const { models, operations } = useHelpSearch({ path: "/help/search" });
  const sections = [];

  for (const [section, content] of Object.entries<Section>(HELP)) {
    if (content.description === "") continue;

    sections.push(
      <Link
        key={section}
        href={`/help/${section}`}
        className="transition-colors ease-in-out duration-500 p-4 hover:bg-memod-grey-based rounded-2xl"
      >
        <h3 className="text-xl font-semibold pb-1">{content.name}</h3>
        <p className="text-lg">{content.description}</p>
      </Link>
    );
  }

  return {
    models: {
      sections,
      searchValue: models.value,
    },
    operations: {
      setSearchValue: operations.setValue,
      onSearch: operations.onSearch,
    }
  }
}

export function ClearSearchIcon(props: ClearSearchProps): JSX.Element {
  return (
    <span onClick={props.onClick}>
      <Icon.Cancel />
    </span>
  );
}

export default function HelpCenterIndex() {
  const { models, operations } = useHelpCenterIndex();

  return (
    <HelpCenterLayout>
      <div>
        <div className="flex flex-col gap-5 items-center justify-center h-[279px] bg-center md:rounded-xl md:mt-8" style={{ backgroundImage: `url(${BackgroundImage.src})` }}>
          <h1 className="text-base md:text-xl">{translate("base.help-center")}</h1>
          <h3 className="font-ZillaSlab text-2xl md:text-6xl font-semibold italic">{translate("title")}</h3>
          <div className=" w-[288px] md:w-[480px]">
            <Input
              value={models.searchValue}
              placeholder={translate("base.type-question")}
              className="!bg-memod-base focus:border-white text-[#9e9fa5]"
              Icon={<Icon.Search />}
              IconClassName="w-[58px] items-center"
              button={models.searchValue && <ClearSearchIcon onClick={() => operations.setSearchValue("")} /> || undefined}
              onChange={(e) => operations.setSearchValue(e.target.value)}
              onKeyDown={operations.onSearch}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 my-16">
          {models.sections}
        </div>
        <div className="h-0.5 bg-gradient-to-r from-transparent via-memod-green-tertiary via-50% to-transparent" />
        <div className="flex items-center justify-center gap-3.5 md:gap-6 md:my-20">
          <h4 className="md:text-3xl">{translate("help.more-questions")}</h4>
          <Link
            href="/help/contact_us"
            className="text-xs md:text-base font-semibold rounded-full px-6 py-2.5 bg-white text-memod-black hover:bg-white/70"
          >
            {translate("help.contact-support")}
          </Link>
        </div>
      </div>
    </HelpCenterLayout>
  );
}